import React, { FC, useState } from 'react';

import { Locale, addDays, format, startOfWeek } from 'date-fns';
import PropTypes, { Validator } from 'prop-types';

import Button, { ButtonGroup } from '@adsk/alloy-react-button';

import { DAYS_OF_THE_WEEK_IN_ENGLISH } from '../helpers/consts';
import { RunOn, DayOfTheWeek } from '../types';

const MAP_ISO_WEEK = [1, 2, 3, 4, 5, 6, 0];

const mapSelectedDays = (
  initialSelectedDays: RunOn[],
  isISOWeek: boolean,
): Record<number, number> => {
  return initialSelectedDays.reduce((days, day) => {
    const dayIndex = DAYS_OF_THE_WEEK_IN_ENGLISH.indexOf(day as DayOfTheWeek);
    const isoDayIndex = isISOWeek ? MAP_ISO_WEEK.indexOf(dayIndex) : dayIndex;

    return { ...days, [isoDayIndex]: isoDayIndex };
  }, {});
};

const formatDay = (
  firstDayOfWeek: Date | number,
  dayIndex: number,
  localeObject: Locale,
) => {
  return format(addDays(firstDayOfWeek, dayIndex), 'eeeee', {
    locale: localeObject,
  }).toLocaleUpperCase();
};

type MultiButtonGroupProps = {
  initialSelectedDays: RunOn[];
  disableWeeklyMultiDay: boolean;
  onChange: (val: RunOn[]) => void;
  todayDate: Date;
  localeObject: Locale;
};

const MultiButtonGroup: FC<MultiButtonGroupProps> = ({
  initialSelectedDays,
  disableWeeklyMultiDay,
  onChange,
  todayDate,
  localeObject,
}) => {
  const weekStartsOn = localeObject.options?.weekStartsOn;
  const isISOWeek = weekStartsOn === 1;
  const firstDayOfWeek = startOfWeek(todayDate, { weekStartsOn });
  const [selectedDays, setSelectedDays] = useState(
    mapSelectedDays(initialSelectedDays, isISOWeek),
  );
  const multiSelection = (dayIndex: number) => {
    if (dayIndex in selectedDays) {
      const { [dayIndex]: _, ...rest } = selectedDays;

      return rest;
    } else {
      return { ...selectedDays, [dayIndex]: dayIndex };
    }
  };
  const handleOnChange = (dayIndex: number) => {
    const newSelectedDays = disableWeeklyMultiDay
      ? { [dayIndex]: dayIndex }
      : multiSelection(dayIndex);
    const selectedDaysKeys = Object.keys(newSelectedDays).map(Number);

    if (selectedDaysKeys.length > 0) {
      setSelectedDays(newSelectedDays);

      const selectedDaysInEnglish = selectedDaysKeys.map((day) => {
        const dayOfWeekIndex = isISOWeek ? MAP_ISO_WEEK[day] : day;

        return DAYS_OF_THE_WEEK_IN_ENGLISH[dayOfWeekIndex];
      });

      onChange(selectedDaysInEnglish);
    }
  };

  return (
    <ButtonGroup value={'selected'}>
      {Array.from({ length: 7 }, (_, dayIndex) => {
        const isSelected = dayIndex in selectedDays;
        const isLastSelected =
          isSelected && Object.keys(selectedDays).length === 1;

        return (
          <Button
            data-testid={`${dayIndex}`}
            key={dayIndex}
            value={isSelected ? 'selected' : ''}
            onClick={() => handleOnChange(dayIndex)}
            style={{
              width: '36px',
              height: '36px',
              ...(isLastSelected && { cursor: 'not-allowed' }),
            }}
          >
            {formatDay(firstDayOfWeek, dayIndex, localeObject)}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

MultiButtonGroup.propTypes = {
  /**
   * Whether or not users can select multiple days for a weekly request
   */
  disableWeeklyMultiDay: PropTypes.bool.isRequired,
  /**
   * Array of selected day(s)
   */
  initialSelectedDays: PropTypes.array.isRequired,
  /**
   * A callback function called when the selected values have changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Today's date object
   */
  todayDate: PropTypes.instanceOf(Date).isRequired,
  /**
   * Locale object provided by date-fns
   */
  localeObject: PropTypes.object.isRequired as Validator<Locale>,
};

export default MultiButtonGroup;
